import {
  buildOptionalParams,
  buildPath,
  buildTemplatePath,
  CrudMode,
  Dialog,
  handleClose,
  ListContext,
  ListContextValue,
  Page,
  PageRoute,
  ScreenType,
  useParams,
  useUnsavedChanges,
  useValidationErrors,
} from "@compass/core";
import { Permission, StudentAbsence, StudentAbsenceEditResource, StudentAbsenceViewResource } from "generated/openApi";
import "moment-timezone";
import { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import ApiService from "services/apiService";
import { StudentAbsencePageBody } from "./StudentAbsencePageBody";
import { StudentAbsencePageHeader } from "./StudentAbsencePageHeader";

const routeName = "studentAbsence";

class Params {
  id: number = 0;
  crudMode: CrudMode = "view";
  screenType: ScreenType = "standard";
}

class OptionalParams {
  studentId?: number = 0;
  schoolYear?: number = 0;
  schoolId?: number = 0;
  termTypeId?: number = 0;
  termNumber?: number = 0;
}

export const studentAbsencePageRoute: PageRoute<Params, OptionalParams> = {
  handle: "Absence",
  templatePath: buildTemplatePath(routeName, Params),
  getPath: (params) => buildPath(routeName, params),
  getOptionalParams: (params) => buildOptionalParams(params),
};

export const StudentAbsencePage = () => {
  const ROOT_PERMISSION: Permission = Permission.StudentAbsence;
  const { params, optionalParams } = useParams(routeName, Params, OptionalParams);
  const navigate = useNavigate();
  const listContext = useContext<ListContextValue>(ListContext);
  const [absence, setAbsence] = useState<StudentAbsence>({} as StudentAbsence);
  const { unsavedChanges, resetUnsavedChanges } = useUnsavedChanges({ trackedItems: [absence] });
  const [viewResource, setViewResource] = useState<StudentAbsenceViewResource>({});
  const [editResource, setEditResource] = useState<StudentAbsenceEditResource>({});
  const { validationErrors, setValidationErrors } = useValidationErrors();

  useEffect(() => {
    fetch(params.id);
    fetchViewResource();
  }, []);

  useEffect(() => {
    if (params.crudMode === "edit") fetchEditResource();
  }, [params.crudMode]);

  const reroute = (id: number, crudMode: CrudMode) =>
    navigate(`../${studentAbsencePageRoute.getPath({ id: id, crudMode: crudMode, screenType: params.screenType })}`);

  const fetch = (id: number) => {
    ApiService.studentAbsences.get(id).then((response) => {
      if (id === 0) {
        if (optionalParams.studentId) response.studentId = optionalParams.studentId;
        if (optionalParams.schoolYear) response.year = optionalParams.schoolYear;
        if (optionalParams.schoolId) response.schoolId = optionalParams.schoolId;
        if (optionalParams.termTypeId) response.termTypeId = optionalParams.termTypeId;
        if (optionalParams.termNumber) response.termNumber = optionalParams.termNumber;
      }

      setAbsence(response);
      resetUnsavedChanges([response]);
    });
  };

  const fetchViewResource = () => {
    ApiService.studentAbsences.getViewResource().then((response) => {
      setViewResource(response);
    });
  };

  const fetchEditResource = () => {
    ApiService.studentAbsences.getEditResource().then((response) => {
      setEditResource(response);
    });
  };

  const pageHeader = (
    <StudentAbsencePageHeader
      rootPermission={ROOT_PERMISSION}
      handle={studentAbsencePageRoute.handle}
      fetch={fetch}
      reroute={reroute}
      absence={absence}
      crudMode={params.crudMode}
      setValidationErrors={setValidationErrors}
      unsavedChanges={unsavedChanges}
    />
  );

  const pageBody = (
    <StudentAbsencePageBody
      crudMode={params.crudMode}
      absence={absence}
      setAbsence={setAbsence}
      viewResource={viewResource}
      editResource={editResource}
      validationErrors={validationErrors}
    />
  );

  return params.screenType === "standard" ? (
    <Page isForm header={pageHeader}>
      {pageBody}
    </Page>
  ) : (
    <Dialog
      isForm
      open={true}
      onClose={() => handleClose(navigate, unsavedChanges)}
      maxWidth="md"
      header={pageHeader}
      content={pageBody}
    />
  );
};
